/** Collision avoidance for live cards that exceed the reference specimen height. */
export interface MeasuredPitchCard {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Preserve reference origins unless live content would cover another card.
 * Work from the goalkeeper upward, keeping every control inside the field.
 * @param cards - Measured boxes in the current viewport's pixel coordinates.
 * @param fieldHeight - Available field height.
 * @returns Safe top coordinates keyed by card identity.
 */
export const resolvePitchCardTops = (
  cards: readonly MeasuredPitchCard[],
  fieldHeight: number
): Readonly<Record<string, number>> => {
  // gap-1 is bound on the position-card subtree (1011:21548).
  const gap = 4;
  const placed = [...cards]
    .sort((a, b) => b.y - a.y)
    .reduce<readonly MeasuredPitchCard[]>((below, card) => {
      const bottom = below
        .filter(
          other =>
            card.x < other.x + other.width && card.x + card.width > other.x
        )
        .reduce((limit, other) => Math.min(limit, other.y - gap), fieldHeight);
      return [
        ...below,
        { ...card, y: Math.max(0, Math.min(card.y, bottom - card.height)) },
      ];
    }, []);
  return Object.fromEntries(placed.map(card => [card.id, card.y]));
};
